import { Settings, UserPlus, MessageSquare, Copy, Folder, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Avatar, AvatarFallback } from '../ui/avatar';

const WorkspaceSettingsDialog = ({
  open,
  onOpenChange,
  workspace,
  members,
  loadingMembers,
  onInvite,
  onOpenChat,
  onDuplicate,
  onMoveToFolder,
  onChangeMemberRole,
  onRemoveMember,
  onDeleteWorkspace
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto dark:bg-slate-800 dark:border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-gray-100">
            <Settings className="w-5 h-5 text-[#4A9CC7]" />
            {workspace?.name} Settings
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Quick Actions */}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={onInvite}
              className="gap-2 dark:border-slate-600 dark:text-gray-300"
            >
              <UserPlus className="w-4 h-4" />
              Invite Members
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenChat}
              className="gap-2 dark:border-slate-600 dark:text-gray-300"
            >
              <MessageSquare className="w-4 h-4" />
              Open Chat
            </Button>
            {workspace?.user_role === 'owner' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onDuplicate}
                  className="gap-2 dark:border-slate-600 dark:text-gray-300"
                >
                  <Copy className="w-4 h-4" />
                  Duplicate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onMoveToFolder}
                  className="gap-2 dark:border-slate-600 dark:text-gray-300"
                  data-testid="move-to-folder-settings-btn"
                >
                  <Folder className="w-4 h-4" />
                  Move to Folder
                </Button>
              </>
            )}
          </div>

          {/* Members List */}
          <div>
            <Label className="text-sm font-medium mb-3 block dark:text-gray-300">Members ({members.length})</Label>
            {loadingMembers ? (
              <div className="text-sm text-muted-foreground py-4 text-center">Loading members...</div>
            ) : members.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">No members yet</div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {members.map(member => (
                  <div 
                    key={member.user_id}
                    className="flex items-center justify-between p-3 bg-muted dark:bg-slate-900 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-[#4A9CC7]/10 text-[#4A9CC7] text-xs">
                          {member.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium dark:text-gray-100">{member.name}</p>
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {member.role === 'owner' ? (
                        <span className="text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-2 py-1 rounded">Owner</span>
                      ) : (
                        <>
                          <Select 
                            value={member.role} 
                            onValueChange={(val) => onChangeMemberRole(member.user_id, val)}
                          >
                            <SelectTrigger className="h-7 w-24 text-xs dark:bg-slate-800 dark:border-slate-600">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="member">Member</SelectItem>
                              <SelectItem value="viewer">Viewer</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                            onClick={() => onRemoveMember(member.user_id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Danger Zone */}
          {workspace?.user_role === 'owner' && (
            <div className="border-t dark:border-slate-700 pt-4">
              <Label className="text-sm font-medium text-red-600 dark:text-red-400 mb-3 block">Danger Zone</Label>
              <Button
                variant="outline"
                size="sm"
                className="border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
                onClick={onDeleteWorkspace}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Workspace
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkspaceSettingsDialog;
